/**
 * Crop Dialog Component
 *
 * Lightweight image cropping UI for selecting a region of an image before upload.
 * Supports configurable aspect ratios, dimension constraints, and canvas-based output.
 *
 * Features:
 * - Aspect ratio presets (1:1, 4:3, 16:9, 3:2, free)
 * - Dimension constraints (min/max width/height)
 * - Canvas-based cropped image export (Blob output)
 * - Radix Dialog with Tailwind styling
 */

import React, { useState, useRef, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AppLogger } from '@/lib/logger'
import type { Logger } from '@/lib/logger'
import type { CropResult, DimensionConstraints } from '@/lib/imageUploadTypes'

// ============================================================================
// Logger (Lazy Initialization)
// ============================================================================

let _log: Logger | null = null

function getLog(): Logger {
  if (!_log) {
    _log = AppLogger.getLogger('lib/upload/CropDialog')
  }
  return _log
}

// ============================================================================
// Aspect Ratio Utilities
// ============================================================================

/**
 * Parse aspect ratio string (e.g., "16:9") to numeric ratio.
 * Returns undefined for "free" (no constraint).
 *
 * @param aspectRatio - String like "1:1", "4:3", "16:9", "3:2", or "free"
 * @returns Numeric ratio (width/height) or undefined for free
 */
function parseAspectRatio(aspectRatio: string): number | undefined {
  if (aspectRatio === 'free') {
    return undefined
  }

  const parts = aspectRatio.split(':')
  if (parts.length === 2) {
    const w = parseFloat(parts[0])
    const h = parseFloat(parts[1])
    if (!isNaN(w) && !isNaN(h) && h !== 0) {
      return w / h
    }
  }

  return undefined
}

// ============================================================================
// CropArea Type
// ============================================================================

/**
 * Represents the crop selection rectangle.
 */
interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

// ============================================================================
// CropDialog Props
// ============================================================================

export interface CropDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** The image file to crop */
  file: File
  /** Dimension constraints and aspect ratio presets */
  config: DimensionConstraints
  /** Called when crop is applied with result */
  onApply: (result: CropResult) => void
  /** Called when dialog is cancelled */
  onCancel: () => void
}

// ============================================================================
// CropDialog Component
// ============================================================================

/**
 * Dialog component for client-side image cropping.
 *
 * Displays the image with an interactive crop area that can be:
 * - Dragged to reposition
 * - Resized to change dimensions
 * - Constrained by configured min/max dimensions
 * - Locked to configured aspect ratio presets
 *
 * On apply: exports cropped region as Blob using canvas toBlob.
 *
 * @param props - Component props
 * @returns JSX element
 *
 * @example
 * <CropDialog
 *   isOpen={true}
 *   file={imageFile}
 *   config={{ minWidth: 200, maxWidth: 2000, aspectRatios: ['1:1', 'free'] }}
 *   onApply={(result) => console.log(result.blob)}
 *   onCancel={() => setOpen(false)}
 * />
 */
export function CropDialog({
  isOpen,
  file,
  config,
  onApply,
  onCancel,
}: CropDialogProps): React.ReactNode {
  const log = getLog()
  const [imageUrl, setImageUrl] = useState<string>('')
  const [imageNaturalWidth, setImageNaturalWidth] = useState(0)
  const [imageNaturalHeight, setImageNaturalHeight] = useState(0)
  const [containerWidth, setContainerWidth] = useState(0)
  const [containerHeight, setContainerHeight] = useState(0)
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 100, height: 100 })
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<string | undefined>()
  const [isDraggingCrop, setIsDraggingCrop] = useState(false)
  const [isDraggingHandle, setIsDraggingHandle] = useState<string | null>(null)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartY, setDragStartY] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // Load image as data URL
  useEffect(() => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const url = e.target?.result as string
      setImageUrl(url)
      log.debug(`Image loaded for cropping: ${file.name}`)
    }
    reader.onerror = () => {
      log.error(`Failed to load image: ${file.name}`)
    }
    reader.readAsDataURL(file)
  }, [file, log])

  // Calculate container size and initialize crop area when image loads
  useEffect(() => {
    if (!imgRef.current || !imageNaturalWidth) return

    const container = containerRef.current
    if (!container) return

    // Set container to fit image with max height
    const maxHeight = 400
    const ratio = imageNaturalWidth / imageNaturalHeight
    let displayWidth = container.clientWidth - 40 // padding
    let displayHeight = displayWidth / ratio

    if (displayHeight > maxHeight) {
      displayHeight = maxHeight
      displayWidth = displayHeight * ratio
    }

    setContainerWidth(Math.round(displayWidth))
    setContainerHeight(Math.round(displayHeight))

    // Initialize crop area to center of image with default size
    const defaultSize = Math.min(displayWidth, displayHeight) * 0.8
    setCropArea({
      x: (displayWidth - defaultSize) / 2,
      y: (displayHeight - defaultSize) / 2,
      width: defaultSize,
      height: defaultSize,
    })

    log.debug(`Container sized: ${displayWidth}x${displayHeight}`)
  }, [imageNaturalWidth, imageNaturalHeight, log])

  // Apply aspect ratio constraint
  const applyAspectRatioConstraint = (area: CropArea, ratio: number | undefined): CropArea => {
    if (!ratio) return area // "free" aspect ratio

    const newArea = { ...area }
    const currentRatio = newArea.width / newArea.height

    if (currentRatio > ratio) {
      newArea.width = newArea.height * ratio
    } else {
      newArea.height = newArea.width / ratio
    }

    return newArea
  }

  // Apply dimension constraints
  const applyDimensionConstraints = (area: CropArea): CropArea => {
    const newArea = { ...area }

    if (config.minWidth && newArea.width < config.minWidth) {
      newArea.width = config.minWidth
    }
    if (config.maxWidth && newArea.width > config.maxWidth) {
      newArea.width = config.maxWidth
    }
    if (config.minHeight && newArea.height < config.minHeight) {
      newArea.height = config.minHeight
    }
    if (config.maxHeight && newArea.height > config.maxHeight) {
      newArea.height = config.maxHeight
    }

    // Keep within container bounds
    if (newArea.x < 0) newArea.x = 0
    if (newArea.y < 0) newArea.y = 0
    if (newArea.x + newArea.width > containerWidth) {
      newArea.x = containerWidth - newArea.width
    }
    if (newArea.y + newArea.height > containerHeight) {
      newArea.y = containerHeight - newArea.height
    }

    return newArea
  }

  // Handle crop area drag
  const handleCropMouseDown = (e: React.MouseEvent) => {
    if (isDraggingHandle) return
    setIsDraggingCrop(true)
    setDragStartX(e.clientX)
    setDragStartY(e.clientY)
  }

  const handleMouseUp = () => {
    setIsDraggingCrop(false)
    setIsDraggingHandle(null)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingCrop && !isDraggingHandle) return

    const deltaX = e.clientX - dragStartX
    const deltaY = e.clientY - dragStartY

    if (isDraggingCrop) {
      let newArea = {
        ...cropArea,
        x: cropArea.x + deltaX,
        y: cropArea.y + deltaY,
      }
      newArea = applyDimensionConstraints(newArea)
      setCropArea(newArea)
    } else if (isDraggingHandle === 'se') {
      // Southeast handle (resize)
      let newArea = {
        ...cropArea,
        width: Math.max(50, cropArea.width + deltaX),
        height: Math.max(50, cropArea.height + deltaY),
      }

      const ratio = parseAspectRatio(selectedAspectRatio || 'free')
      newArea = applyAspectRatioConstraint(newArea, ratio)
      newArea = applyDimensionConstraints(newArea)
      setCropArea(newArea)
    }

    setDragStartX(e.clientX)
    setDragStartY(e.clientY)
  }

  // Switch aspect ratio preset
  const switchAspectRatio = (aspectRatio: string) => {
    setSelectedAspectRatio(aspectRatio)
    const ratio = parseAspectRatio(aspectRatio)
    let newArea = applyAspectRatioConstraint(cropArea, ratio)
    newArea = applyDimensionConstraints(newArea)
    setCropArea(newArea)
  }

  // Export cropped image
  const handleApplyCrop = async () => {
    const log = getLog()

    try {
      // Create canvas matching natural image dimensions
      const canvas = document.createElement('canvas')
      const scaleX = imageNaturalWidth / containerWidth
      const scaleY = imageNaturalHeight / containerHeight

      canvas.width = Math.round(cropArea.width * scaleX)
      canvas.height = Math.round(cropArea.height * scaleY)

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Could not get canvas context')
      }

      const img = new Image()
      img.onload = () => {
        ctx.drawImage(
          img,
          cropArea.x * scaleX,
          cropArea.y * scaleY,
          cropArea.width * scaleX,
          cropArea.height * scaleY,
          0,
          0,
          canvas.width,
          canvas.height
        )

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              log.error('Failed to export cropped image')
              return
            }

            log.debug(
              `Crop exported: ${canvas.width}x${canvas.height}, ${blob.size} bytes`
            )

            onApply({
              blob,
              width: canvas.width,
              height: canvas.height,
            })
          },
          'image/jpeg',
          0.95
        )
      }
      img.onerror = () => {
        log.error('Failed to load image for canvas export')
      }
      img.src = imageUrl
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      log.error(`Error exporting crop: ${message}`)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {/* Image Container */}
          <div
            ref={containerRef}
            className="relative bg-gray-100 flex items-center justify-center mx-auto p-5 border border-gray-300 rounded"
            style={{ minHeight: '300px' }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {imageUrl && (
              <>
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop preview"
                  onLoad={(e) => {
                    const img = e.currentTarget
                    setImageNaturalWidth(img.naturalWidth)
                    setImageNaturalHeight(img.naturalHeight)
                  }}
                  className="max-w-full max-h-full pointer-events-none"
                  style={{
                    width: `${containerWidth}px`,
                    height: `${containerHeight}px`,
                  }}
                />

                {/* Crop Area Overlay */}
                {containerWidth > 0 && (
                  <div
                    className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-10 cursor-move"
                    style={{
                      left: `${cropArea.x}px`,
                      top: `${cropArea.y}px`,
                      width: `${cropArea.width}px`,
                      height: `${cropArea.height}px`,
                    }}
                    onMouseDown={handleCropMouseDown}
                  >
                    {/* Dimension Display */}
                    <div className="absolute bottom-1 left-1 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                      {Math.round(cropArea.width)} × {Math.round(cropArea.height)}
                    </div>

                    {/* Resize Handle */}
                    <div
                      className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize"
                      onMouseDown={(e) => {
                        e.stopPropagation()
                        setIsDraggingHandle('se')
                        setDragStartX(e.clientX)
                        setDragStartY(e.clientY)
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Aspect Ratio Presets */}
          {config.aspectRatios && config.aspectRatios.length > 0 && (
            <div className="mt-4 flex gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-700 self-center">
                Aspect Ratio:
              </span>
              {config.aspectRatios.map((ar) => (
                <Button
                  key={ar}
                  variant={selectedAspectRatio === ar ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => switchAspectRatio(ar)}
                  className="text-xs"
                >
                  {ar}
                </Button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleApplyCrop}>
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
